import api from 'api'
import { queryParamSerializer } from 'lib/api/queryParamSerializer'

export type AdminChargeItemWorkClocks = {
  startedAt: string | null
  completedAt: string | null
}

export type AdminChargeItemWorkBreak = {
  lunchedAt: string | null
  durationMinutes: number | null
}

export type AdminChargeItemWorkPayBreakdown = {
  wallClockDurationMinutes: number
  minPayLengthMinutes: number
  unpaidLunchBreakLengthMinutes: number
  totalWorkDurationMinutes: number
}

export type AdminChargeW2PayrollFrequency = 'daily' | 'weekly'

export type AdminChargeItemWork = {
  id: number
  workerId: number
  workerName: string | null
  w2PayrollFrequency: AdminChargeW2PayrollFrequency | null
  clocks: AdminChargeItemWorkClocks
  break: AdminChargeItemWorkBreak
  payBreakdown: AdminChargeItemWorkPayBreakdown | null
}

export type AdminChargeItem = {
  id: number
  amount: string | number
  quantity: string | number
  unitAmount: string | number
  description: string
  itemType: string
  work: AdminChargeItemWork | null
  createdAt: string
}

export type AdminChargeShiftLocation = {
  id: number
  name: string
}

export type AdminChargeShiftPosition = {
  id: number
  name: string
}

export type AdminChargeShift = {
  id: number
  startsAt: string
  endsAt: string
  timezone: string | null
  location: AdminChargeShiftLocation
  position: AdminChargeShiftPosition
}

export type AdminChargeInvoice = {
  id: number
  status: string
  automated: boolean
  memo: string | null
  stripeId: string | null
  totalAmount: string | number
  createdAt: string
}

export type AdminChargeInvoiceConfig = {
  cycleType: 'standard' | 'weekly'
  companyId: number | null
  companyGroupId: number | null
  breakdownLevels: string[]
  customWeekStartDay: string | null
  customWeekTimezone: string | null
}

export type AdminChargeStagingStatus = 'ready' | 'staged' | 'not_ready'

export type AdminChargeUninvoiced = {
  stagingStatus: AdminChargeStagingStatus
  runAt: string | null
  invoiceGroupKey: string[]
  invoiceConfig: AdminChargeInvoiceConfig
}

export type AdminCharge = {
  id: number
  companyId: number
  finalized: boolean
  totalAmount: string | number
  createdAt: string
  items: AdminChargeItem[]
  shift: AdminChargeShift | null
}

export type AdminChargeGroupKind = 'invoice' | 'uninvoiced'

export type AdminChargeGroup = {
  kind: AdminChargeGroupKind
  invoice: AdminChargeInvoice | null
  uninvoiced: AdminChargeUninvoiced | null
  charges: AdminCharge[]
}

export type GetChargesRequest = {
  page: number
  pageSize: number
  companyId?: number
  shiftId?: number
  workerId?: number
  invoiceId?: number
  invoiced?: boolean
}

export type GetChargesResponse = {
  items: AdminChargeGroup[]
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

export async function getCharges(
  request: GetChargesRequest
): Promise<GetChargesResponse> {
  const response = await api.get<GetChargesResponse>('/admin/charges', {
    params: {
      page: request.page,
      pageSize: request.pageSize,
      companyId: request.companyId,
      shiftId: request.shiftId,
      workerId: request.workerId,
      invoiceId: request.invoiceId,
      invoiced: request.invoiced,
    },
    paramsSerializer: queryParamSerializer,
  })
  return response.data
}
